import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, ExternalLink, FileText, Film, X } from 'lucide-react';
import { openUrlInNewTab } from '@/config/ipeRoutes';
import PlatformTutorialMarkdownPreview from '@/components/platform-tutorial/PlatformTutorialMarkdownPreview';
import PlatformTutorialPptxPreview from '@/components/platform-tutorial/PlatformTutorialPptxPreview';
import PlatformTutorialDocxPreview from '@/components/platform-tutorial/PlatformTutorialDocxPreview';
import PlatformTutorialSpreadsheetPreview from '@/components/platform-tutorial/PlatformTutorialSpreadsheetPreview';
import '@/styles/ipe/commerce/PlatformTutorialPreviewModal.css';

function detectKind({ type, mimeType, fileName, url }) {
  const t = String(type || '').toUpperCase();
  const mime = String(mimeType || '').toLowerCase();
  const name = `${fileName || ''} ${url || ''}`.toLowerCase();

  if (t === 'VIDEO' || mime.startsWith('video/') || /\.(mp4|webm|mov)(\?|#|$)/i.test(name)) {
    return 'video';
  }
  if (mime === 'application/pdf' || /\.pdf(\?|#|$)/i.test(name)) return 'pdf';
  if (mime === 'text/markdown' || /\.(md|markdown)(\?|#|$)/i.test(name)) return 'markdown';
  if (mime === 'text/csv' || mime.includes('spreadsheet') || mime.includes('excel')
    || /\.(xlsx|xls|csv)(\?|#|$)/i.test(name)) {
    return 'sheet';
  }
  if (mime.startsWith('text/') || /\.txt(\?|#|$)/i.test(name)) return 'text';
  if (mime.includes('presentationml') || /\.pptx(\?|#|$)/i.test(name)) return 'pptx';
  if (mime === 'application/vnd.ms-powerpoint' || (/\.ppt(\?|#|$)/i.test(name) && !/\.pptx(\?|#|$)/i.test(name))) {
    return 'pptLegacy';
  }
  if (mime.includes('wordprocessingml') || /\.docx(\?|#|$)/i.test(name)) return 'docx';
  if (mime === 'application/msword' || (/\.doc(\?|#|$)/i.test(name) && !/\.docx(\?|#|$)/i.test(name))) {
    return 'docLegacy';
  }
  if (t === 'DOCUMENT') return 'office';
  return 'unknown';
}

function guessDownloadName(fileName, title, kind) {
  const raw = String(fileName || '').trim();
  if (raw) return raw;
  const base = String(title || 'tutorial').trim() || 'tutorial';
  if (kind === 'pdf') return `${base}.pdf`;
  if (kind === 'video') return `${base}.mp4`;
  if (kind === 'markdown') return `${base}.md`;
  if (kind === 'text') return `${base}.txt`;
  if (kind === 'docx' || kind === 'docLegacy') return `${base}.docx`;
  if (kind === 'sheet') return `${base}.xlsx`;
  if (kind === 'pptx' || kind === 'pptLegacy') return `${base}.pptx`;
  return base;
}

/**
 * 平台教程在线预览：视频 / PDF / MD / DOCX / Excel / PPTX 等。
 */
export default function PlatformTutorialPreviewModal({
  open = false,
  title = '教程预览',
  url = '',
  type = 'VIDEO',
  mimeType = '',
  fileName = '',
  onClose,
}) {
  const kind = useMemo(
    () => detectKind({ type, mimeType, fileName, url }),
    [type, mimeType, fileName, url],
  );
  const [textContent, setTextContent] = useState('');
  const [textError, setTextError] = useState('');
  const [textLoading, setTextLoading] = useState(false);
  const [pdfObjectUrl, setPdfObjectUrl] = useState('');
  const [pdfError, setPdfError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    let alive = true;
    let objectUrl = '';
    if (!open || kind !== 'pdf' || !url) {
      setPdfObjectUrl('');
      setPdfError('');
      setPdfLoading(false);
      return undefined;
    }
    setPdfLoading(true);
    setPdfError('');
    setPdfObjectUrl('');
    fetch(url)
      .then(async (res) => {
        if (!res.ok) throw new Error(`加载失败（${res.status}）`);
        const blob = await res.blob();
        const pdfBlob = blob.type === 'application/pdf'
          ? blob
          : new Blob([blob], { type: 'application/pdf' });
        objectUrl = URL.createObjectURL(pdfBlob);
        if (alive) setPdfObjectUrl(objectUrl);
      })
      .catch((e) => {
        if (alive) {
          setPdfObjectUrl('');
          setPdfError(e?.message || 'PDF 加载失败');
        }
      })
      .finally(() => {
        if (alive) setPdfLoading(false);
      });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, kind, url]);

  useEffect(() => {
    let alive = true;
    if (!open || (kind !== 'text' && kind !== 'markdown') || !url) {
      setTextContent('');
      setTextError('');
      setTextLoading(false);
      return undefined;
    }
    setTextLoading(true);
    setTextError('');
    fetch(url)
      .then(async (res) => {
        if (!res.ok) throw new Error(`加载失败（${res.status}）`);
        const text = await res.text();
        if (alive) setTextContent(text);
      })
      .catch((e) => {
        if (alive) {
          setTextContent('');
          setTextError(e?.message || '文档加载失败');
        }
      })
      .finally(() => {
        if (alive) setTextLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open, kind, url]);

  const safeUrl = String(url || '').trim();
  const displayName = fileName || title || '文件';
  const downloadName = guessDownloadName(fileName, title, kind);

  const openExternal = () => {
    if (!safeUrl || kind === 'video') return;
    const target = kind === 'pdf' && pdfObjectUrl ? pdfObjectUrl : safeUrl;
    openUrlInNewTab(target);
  };

  const handleDownload = useCallback(async () => {
    if (!safeUrl || downloading) return;
    setDownloading(true);
    try {
      let objectUrl = '';
      let revoke = false;
      if (kind === 'pdf' && pdfObjectUrl) {
        objectUrl = pdfObjectUrl;
      } else {
        const res = await fetch(safeUrl);
        if (!res.ok) throw new Error(`下载失败（${res.status}）`);
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        revoke = true;
      }
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = downloadName;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      if (revoke) URL.revokeObjectURL(objectUrl);
    } catch (e) {
      openUrlInNewTab(safeUrl);
    } finally {
      setDownloading(false);
    }
  }, [safeUrl, downloading, kind, pdfObjectUrl, downloadName]);

  if (!open || typeof document === 'undefined') return null;

  const renderBody = () => {
    if (!safeUrl) return <div className="pt-preview__empty">暂无可预览文件</div>;

    if (kind === 'video') {
      return (
        <video
          key={safeUrl}
          className="pt-preview__video"
          src={safeUrl}
          controls
          autoPlay
          playsInline
          preload="metadata"
        />
      );
    }

    if (kind === 'pdf') {
      if (pdfLoading) return <div className="pt-preview__empty">文档加载中…</div>;
      if (pdfError) {
        return (
          <div className="pt-preview__fallback">
            <p>{pdfError}</p>
            <div className="pt-preview__fallback-actions">
              <button type="button" className="pt-preview__cta" onClick={handleDownload}>
                <Download size={15} aria-hidden />
                下载文件
              </button>
            </div>
          </div>
        );
      }
      return (
        <iframe
          className="pt-preview__frame"
          title={displayName}
          src={pdfObjectUrl}
        />
      );
    }

    if (kind === 'markdown' || kind === 'text') {
      if (textLoading) return <div className="pt-preview__empty">文档加载中…</div>;
      if (textError) {
        return (
          <div className="pt-preview__fallback">
            <p>{textError}</p>
            <button type="button" className="pt-preview__cta" onClick={handleDownload}>
              <Download size={15} aria-hidden />
              下载文件
            </button>
          </div>
        );
      }
      if (kind === 'markdown') {
        return <PlatformTutorialMarkdownPreview content={textContent} />;
      }
      return <pre className="pt-preview__text">{textContent}</pre>;
    }

    if (kind === 'docx') {
      return <PlatformTutorialDocxPreview url={safeUrl} onDownload={handleDownload} />;
    }

    if (kind === 'sheet') {
      return (
        <PlatformTutorialSpreadsheetPreview
          url={safeUrl}
          fileName={displayName}
          onDownload={handleDownload}
        />
      );
    }

    if (kind === 'pptx') {
      return <PlatformTutorialPptxPreview url={safeUrl} />;
    }

    if (kind === 'docLegacy') {
      return (
        <div className="pt-preview__fallback">
          <FileText size={36} aria-hidden />
          <p>
            「{displayName}」为旧版 Word（.doc）格式，浏览器无法在线渲染。
            请另存为 .docx / PDF 后上传，或下载后本地打开。
          </p>
          <div className="pt-preview__fallback-actions">
            <button type="button" className="pt-preview__cta" onClick={handleDownload}>
              <Download size={15} aria-hidden />
              下载文件
            </button>
          </div>
        </div>
      );
    }

    if (kind === 'pptLegacy') {
      return (
        <div className="pt-preview__fallback">
          <FileText size={36} aria-hidden />
          <p>
            「{displayName}」为旧版 PPT（.ppt）格式，浏览器无法在线渲染。
            请上传 .pptx，或下载后用 PowerPoint / WPS 打开。
          </p>
          <div className="pt-preview__fallback-actions">
            <button type="button" className="pt-preview__cta" onClick={handleDownload}>
              <Download size={15} aria-hidden />
              下载文件
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="pt-preview__fallback">
        <FileText size={36} aria-hidden />
        <p>
          「{displayName}」暂不支持在线预览。
          推荐：PDF / DOCX / Markdown / PPTX / Excel（xlsx/xls/csv）。
        </p>
        <div className="pt-preview__fallback-actions">
          <button type="button" className="pt-preview__cta" onClick={handleDownload}>
            <Download size={15} aria-hidden />
            下载文件
          </button>
        </div>
      </div>
    );
  };

  return createPortal(
    <div className="pt-preview" role="presentation">
      <button
        type="button"
        className="pt-preview__backdrop"
        aria-label="关闭预览"
        onClick={() => onClose?.()}
      />
      <div
        className={`pt-preview__panel pt-preview__panel--${kind}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="pt-preview__head">
          <div className="pt-preview__title-wrap">
            {kind === 'video' ? <Film size={16} aria-hidden /> : <FileText size={16} aria-hidden />}
            <h3 className="pt-preview__title">{title || '教程预览'}</h3>
          </div>
          <div className="pt-preview__actions">
            {safeUrl ? (
              <>
                <button
                  type="button"
                  className="pt-preview__icon-btn"
                  onClick={handleDownload}
                  disabled={downloading}
                  title="下载"
                >
                  <Download size={16} aria-hidden />
                  <span>{downloading ? '下载中…' : '下载'}</span>
                </button>
                {kind !== 'video' ? (
                  <button type="button" className="pt-preview__icon-btn" onClick={openExternal} title="新窗口打开">
                    <ExternalLink size={16} aria-hidden />
                    <span>新窗口</span>
                  </button>
                ) : null}
              </>
            ) : null}
            <button type="button" className="pt-preview__icon-btn" onClick={() => onClose?.()} aria-label="关闭">
              <X size={18} />
            </button>
          </div>
        </header>

        <div className="pt-preview__body">
          {renderBody()}
        </div>
      </div>
    </div>,
    document.body,
  );
}
